import { ALL_PRODUCTS } from "../../constants";
import {
  FolioWiseHolding,
  SchemeWiseHolding,
  SubmittedOrder,
  PortfolioSummary,
  SchemeFolioHolding,
  PortfolioBreakdown,
} from "../../types";
import { api } from "../axios";

export async function getHoldingsData(
  investmentAccountGuid: string
): Promise<SchemeWiseHolding[]> {
  return api.get(`holdings/scheme-wise/`, {
    params: { investment_account_guid: investmentAccountGuid },
  });
}

export async function getPortfolioSummary(
  investmentAccountGuid: string
): Promise<PortfolioSummary[]> {
  return api.get(`portfolio-summary/`, {
    params: { investment_account_guid: investmentAccountGuid },
  });
}

export async function getPortfolioSummaryV2(
  investmentAccountGuid: string
): Promise<PortfolioSummary[]> {
  return api.get(`v2/portfolio-summary/`, {
    params: { investment_account_guid: investmentAccountGuid },
  });
}

export interface GetTransactionsResponse {
  next: string;
  previous: string;
  results: SubmittedOrder[];
  count: number;
}

export async function getSubmittedOrders({
  page = 1,
  pageSize = 10,
  investment_account_guid,
  status,
  isin,
  from_date,
  to_date,
  orderType,
  productType,
  search,
  include_family_data,
}: {
  page?: number;
  pageSize?: number;
  /** Omitted when `include_family_data` asks for the whole family's orders. */
  investment_account_guid?: string;
  isin?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
  orderType?: string;
  search?: string;
  productType?: string;
  /** Investor-only: orders of every account in the caller's family. */
  include_family_data?: boolean;
}): Promise<GetTransactionsResponse> {
  return api.get("submitted-orders/", {
    params: {
      page,
      page_size: pageSize,
      investment_account_guid,
      isin,
      status,
      from_date,
      to_date,
      order_type: orderType,
      product_type: productType ?? ALL_PRODUCTS.join(","),
      search,
      include_family_data,
    },
  });
}

export async function getUpcomingOrders({
  page = 1,
  pageSize = 10,
  fromDate,
  toDate,
  investmentAccountId,
}: {
  page?: number;
  pageSize?: number;
  fromDate?: string;
  toDate?: string;
  investmentAccountId?: string;
}) {
  return api.get("upcoming-orders/", {
    params: {
      page,
      page_size: pageSize,
      from_date: fromDate,
      to_date: toDate,
      investment_account_guid: investmentAccountId,
    },
  });
}

export function getHoldingFolios(
  investmentAccountGuid: string,
  schemeIsin: string
): Promise<FolioWiseHolding[]> {
  return api.get(`holdings/folio-wise/`, {
    params: {
      investment_account_guid: investmentAccountGuid,
      scheme_isin: schemeIsin,
    },
  });
}

export function getSubmittedOrderById(
  id: string | number,
  investmentAccountGuid: string
): Promise<SubmittedOrder> {
  return api.get(`submitted-orders/${id}/`, {
    params: {
      investment_account_guid: investmentAccountGuid,
    },
  });
}

export interface CancelSubmittedOrderPayload {
  investment_account_guid: string;
  remarks?: string;
}

export function cancelSubmittedOrderApi(
  id: string | number,
  payload: CancelSubmittedOrderPayload
) {
  // Backend returns 204 No Content, so the response body is intentionally ignored.
  return api
    .post(`submitted-orders/${id}/cancel/`, payload)
    .then(() => undefined);
}

export function transformSchemeFolioHoldings(data: Array<SchemeFolioHolding>) {
  return data.map((d) => ({
    ...d,
    folio_number:
      d.folio_data && d.folio_data.length === 1
        ? d.folio_data[0].folio_number
        : "",
    folio_data: d.folio_data?.map((fd) => ({
      ...fd,
      scheme_isin: d.scheme_isin,
    })),
  }));
}

export async function getSchemeFolioHoldingsData(
  investmentAccountId: string,
  empanelled?: boolean,
  search?: string
): Promise<Array<SchemeFolioHolding>> {
  const response = await api.get(`holdings/scheme-folio-wise/`, {
    params: {
      investment_account_guid: investmentAccountId,
      empanelled,
      search: search || undefined,
    },
  });
  return transformSchemeFolioHoldings(response.scheme_data);
}

export async function getPortfolioCategorySummary(payload: {
  investmentAccountId?: string;
  familyId?: number;
}): Promise<PortfolioBreakdown[]> {
  return api.get("portfolio-category-summary/", {
    params: {
      ...(payload.investmentAccountId
        ? { investment_account_id: payload.investmentAccountId }
        : {}),
      ...(payload.familyId ? { family_id: payload.familyId } : {}),
    },
  });
}
